import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Alert, IconButton, TextField, Tooltip,
  Button, Grid, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  CircularProgress, useMediaQuery, useTheme
} from '@mui/material';
import { Home, Star, Add, Edit, Delete, WarningAmber, Tune, AutoFixHigh } from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

// Suggested categories for a grading system. Free text is also allowed via edit.
const CATEGORIES = ['Language', 'Maths & Science', 'Humanities & Technicals', 'Overall', 'CBC', 'CBE'];

const emptyCreate = { name: '', category: '', copyFrom: '' };

const GradingSystemsPage = () => {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [systems, setSystems] = useState([]);   // [{ system, category, bandCount }]
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [seeding, setSeeding] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [editRow,    setEditRow]    = useState(null); // { system, category, newName }
  const [confirmDel, setConfirmDel] = useState(null); // system row pending delete

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/academics/grading-systems`, { headers });
      setSystems(data || []);
    } catch { setError('Failed to load grading systems'); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3500); };

  const handleCreate = async () => {
    const name = createForm.name.trim();
    if (!name) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/api/academics/grading-systems`, {
        name, category: createForm.category.trim(), copyFrom: createForm.copyFrom || undefined,
      }, { headers });
      setCreateOpen(false); setCreateForm(emptyCreate);
      await load();
      flash(`Created "${name}". Add or fine-tune its bands next.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create grading system');
    } finally { setSaving(false); }
  };

  const handleEditSave = async () => {
    const { system, newName, category } = editRow;
    setSaving(true); setError('');
    try {
      await axios.put(`${API}/api/academics/grading-systems/${encodeURIComponent(system)}`,
        { category, newName: newName && newName.trim() !== system ? newName.trim() : undefined },
        { headers });
      setEditRow(null);
      await load();
      flash('Grading system updated');
    } catch (err) {
      setError(err.response?.data?.error || 'Update failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const sys = confirmDel.system; setConfirmDel(null); setError('');
    try {
      await axios.delete(`${API}/api/academics/grading-systems/${encodeURIComponent(sys)}`, { headers });
      await load();
      flash(`Deleted "${sys}". Subjects using it fall back to their stage curriculum.`);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/academics/grading-scale/seed-defaults`, {}, { headers });
      await load();
      flash(data.inserted ? `Seeded ${data.inserted} default band(s) (CBC + 8-4-4)` : 'Defaults already present');
    } catch (err) { setError(err.response?.data?.error || 'Seeding failed'); }
    finally { setSeeding(false); }
  };

  const manageBands = (sys) => navigate(`/setups/grading-scale?system=${encodeURIComponent(sys)}`);

  const Actions = ({ row }) => (
    <Box display="flex" gap={0.5} justifyContent="flex-end">
      <Tooltip title="Manage bands">
        <IconButton size="small" color="primary" onClick={() => manageBands(row.system)}><Tune fontSize="small" /></IconButton>
      </Tooltip>
      <Tooltip title="Rename / set category">
        <IconButton size="small" onClick={() => setEditRow({ system: row.system, category: row.category || '', newName: row.system })}><Edit fontSize="small" /></IconButton>
      </Tooltip>
      <Tooltip title="Delete grading system">
        <IconButton size="small" color="error" onClick={() => setConfirmDel(row)}><Delete fontSize="small" /></IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', mt: { xs: 2, sm: 4 }, mb: 4, px: { xs: 1, sm: 0 } }}>
      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Star sx={{ color: '#1565c0' }} />
              <Typography variant="h5" color="primary">Grading Systems</Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button variant="contained" size="small" startIcon={<Add />}
                onClick={() => { setCreateForm(emptyCreate); setError(''); setCreateOpen(true); }}>
                Create Grading System
              </Button>
              <Tooltip title="Seed the standard CBC & 8-4-4 bands (won't overwrite existing grades)">
                <span>
                  <Button variant="outlined" size="small" startIcon={<AutoFixHigh />} onClick={handleSeedDefaults} disabled={seeding}>
                    {seeding ? 'Seeding…' : 'Seed Defaults'}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Back to Academics">
                <IconButton onClick={() => navigate('/academics')} sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Home />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Each grading system is a named set of grade bands with a category (e.g. Languages, Maths &amp; Science).
            Create and organise them here, then assign a system to each subject under <strong>Stage Subjects</strong>.
            Use <strong>Manage bands</strong> to edit a system's grade boundaries.
          </Typography>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box textAlign="center" py={4}><CircularProgress size={28} /></Box>
          ) : systems.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" mb={1}>No grading systems yet.</Typography>
              <Typography variant="caption" color="text.secondary">
                Click <strong>Seed Defaults</strong> for the standard CBC / 8-4-4 scales, or <strong>Create Grading System</strong> to add your own.
              </Typography>
            </Box>
          ) : isMobile ? (
            <Box display="flex" flexDirection="column" gap={1.2}>
              {systems.map(row => (
                <Card key={row.system} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>{row.system}</Typography>
                      <Actions row={row} />
                    </Box>
                    <Box display="flex" gap={1} mt={0.5} flexWrap="wrap" alignItems="center">
                      {row.category
                        ? <Chip label={row.category} size="small" color="info" variant="outlined" />
                        : <Chip label="No category" size="small" variant="outlined" />}
                      <Typography variant="caption" color="text.secondary">{row.bandCount} band{row.bandCount !== 1 ? 's' : ''}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 560 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Grading System</strong></TableCell>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell align="center"><strong>Bands</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {systems.map(row => (
                    <TableRow key={row.system} hover>
                      <TableCell><strong>{row.system}</strong></TableCell>
                      <TableCell>
                        {row.category
                          ? <Chip label={row.category} size="small" color="info" variant="outlined" />
                          : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell align="center">{row.bandCount}</TableCell>
                      <TableCell align="right"><Actions row={row} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Create dialog ── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Grading System</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Name *" value={createForm.name} fullWidth size="small"
                onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                helperText="e.g. MATHS AND SCIENCES" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Category" value={createForm.category} fullWidth size="small"
                onChange={e => setCreateForm(f => ({ ...f, category: e.target.value }))}>
                <MenuItem value=""><em>No category</em></MenuItem>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select label="Start from (copy bands)" value={createForm.copyFrom} fullWidth size="small"
                onChange={e => setCreateForm(f => ({ ...f, copyFrom: e.target.value }))}
                helperText="Copy an existing system's bands as a starting point, or start blank and add bands later">
                <MenuItem value=""><em>Start blank</em></MenuItem>
                {systems.map(s => <MenuItem key={s.system} value={s.system}>{s.system}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>{saving ? 'Creating…' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit (rename / category) dialog ── */}
      <Dialog open={Boolean(editRow)} onClose={() => setEditRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Grading System</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Name" value={editRow?.newName || ''} fullWidth size="small"
                onChange={e => setEditRow(r => ({ ...r, newName: e.target.value }))}
                helperText="Renaming keeps existing subject assignments pointing at the new name" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Category" value={CATEGORIES.includes(editRow?.category) ? editRow?.category : (editRow?.category ? '__custom' : '')}
                fullWidth size="small"
                onChange={e => { if (e.target.value !== '__custom') setEditRow(r => ({ ...r, category: e.target.value })); }}>
                <MenuItem value=""><em>No category</em></MenuItem>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                {editRow?.category && !CATEGORIES.includes(editRow.category) && (
                  <MenuItem value="__custom" disabled>{editRow.category} (custom)</MenuItem>
                )}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirm delete ── */}
      <Dialog open={Boolean(confirmDel)} onClose={() => setConfirmDel(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Delete Grading System
        </DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{confirmDel?.system}</strong> and all its {confirmDel?.bandCount} band(s)? Subjects assigned
            to it will fall back to their stage's curriculum until reassigned.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GradingSystemsPage;
